import React, { Component } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { isEmpty } from "lodash";
import { BUTTON_TYPE, Icons } from "prefab";
import { FORM_FIELD_TYPE, REASON_TYPES } from "../../../../constants";
import { getAllReasonsByType } from "../../../../api";
import RightPanel from "../../../common/RightPanel";
import Form from "../../../common/Form";
import InfoBlock from "../../../common/InfoBlock";
import formatValidityPeriodDisplay from "../../../../utils/formatValidityPeriodDisplay";
import BulkActionButtonsStyles from "../../../../styles/BulkActionButtons.module.scss";
import styles from "../../../../styles/Campaigns/PauseOrResumeActions.module.scss";

const { InfoFilledIcon } = Icons;

const SCHEDULE_TYPE = { IMMEDIATE: "immediate", SELECT_DATE: "selectDate" };

export default class CancelCampaigns extends Component {
  state = {
    fromDate: this.props.fromDate,
    scheduleType: SCHEDULE_TYPE.SELECT_DATE,
    hasError: false,
    reason: null,
    comments: "",
  };

  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    cancelCampaigns: PropTypes.func.isRequired,
    reasonType: PropTypes.string,
  };

  static defaultProps = {
    fromDate: null,
    selectionInfo: null,
  };

  componentWillUnmount = () => {
    this.cancelAction();
  };

  cancelAction = () => {
    this.setState({
      fromDate: null,
      scheduleType: SCHEDULE_TYPE.SELECT_DATE,
      hasError: false,
      reason: null,
      comments: "",
    });
    this.props.onClose();
  };

  save = () => {
    const { fromDate, scheduleType, reason, comments } = this.state;
    const isImmediate = scheduleType === SCHEDULE_TYPE.IMMEDIATE;
    if (!isImmediate && !fromDate) {
      this.setState({ hasError: true });
      return;
    }
    if (!reason) {
      this.setState({ hasError: true });
      return;
    }
    const data = isImmediate
      ? { clearCancellation: false, reasonId: reason, comments, isImmediate: true }
      : { fromDate, clearCancellation: false, reasonId: reason, comments, isImmediate: false };
    this.props.cancelCampaigns(data);
  };

  inputOnChange = (value, accessor) =>
    this.setState({
      [accessor]: value,
    });

  render = () => {
    const { isOpen, header, selectionInfo, actionType, selected } = this.props;
    const { fromDate, scheduleType, hasError, reason, comments } = this.state;
    const isMedia = this.props.reasonType === REASON_TYPES.mediaCancel;
    const isImmediate = scheduleType === SCHEDULE_TYPE.IMMEDIATE;

    const maxDate = selected
      ? new Date(
          moment.min(selected?.map((campaign) => moment(campaign?.validity?.toDate).endOf("day")))
        )
      : undefined;
    const todayStart = moment().startOf("day").toDate();
    const minDate = todayStart;

    const calloutText = isImmediate
      ? isMedia
        ? "This media will be cancelled before the next scheduled show begins."
        : "The campaign will be cancelled before the next scheduled show begins."
      : isMedia
      ? "This media will be cancelled at end of day for each theatre based on their local theatre timezone."
      : "This campaign will be cancelled at end of day for each theatre based on their local theatre timezone.";

    return (
      <RightPanel
        isOpen={isOpen}
        onClose={() => this.cancelAction()}
        header={header}
        showFooter={true}
        primaryButtonProps={{
          text: isMedia ? "Button.cancelMedia" : "Button.cancelCampaign",
          buttonType: BUTTON_TYPE.PRIMARY,
          onClick: this.save,
        }}
        secondaryButtonProps={{ text: "Button.cancel", onClick: this.cancelAction }}
      >
        <div className={styles.panelBody}>
          <div className={styles.formSection}>
            {!isEmpty(selectionInfo) && (
              <div className={BulkActionButtonsStyles.selectionInfoContainer}>
                <div className={BulkActionButtonsStyles.selectionInfo}>
                  <span>{selectionInfo.count}</span> {selectionInfo.label} Selected
                </div>
              </div>
            )}
            {selected?.length === 1 && (
              <InfoBlock
                hideHeader
                cardType="secondary"
                className={styles.selectedItemInfo}
                config={[
                  {
                    title: isMedia ? "Media Name & ID" : "Campaign Name & ID",
                    value: `${selected[0].name} (${
                      isMedia ? selected[0].mediaNumber : selected[0].campaignNumber
                    })`,
                    size: { lg: 12, md: 12, sm: 12 },
                  },
                  {
                    title: "Validity",
                    value: formatValidityPeriodDisplay(selected[0].validity, "DD MMM YYYY"),
                    size: { lg: 12, md: 12, sm: 12 },
                  },
                ]}
              />
            )}
            <Form
              config={[
                {
                  type: FORM_FIELD_TYPE.RADIO,
                  title: "Schedule Type",
                  radioList: [
                    { label: "Immediate", value: SCHEDULE_TYPE.IMMEDIATE },
                    { label: "Select Date", value: SCHEDULE_TYPE.SELECT_DATE },
                  ],
                  value: scheduleType,
                  onChange: (v) => this.setState({ scheduleType: v, hasError: false }),
                },
                {
                  type: FORM_FIELD_TYPE.DATE,
                  title: `${actionType} On`,
                  isHidden: isImmediate,
                  invalidDateMessage: "Invalid Date",
                  onChange: (v) => {
                    this.setState({ fromDate: moment(v).format("YYYY-MM-DD") });
                  },
                  value: fromDate ? new Date(fromDate) : null,
                  placeholder: "Select Date",
                  minDate: minDate,
                  error: hasError && !isImmediate && !fromDate,
                  errorMessage: "Enter Valid Date",
                  maxDate: maxDate,
                  disabled: minDate > maxDate,
                  defaultValue: minDate,
                },
                {
                  type: FORM_FIELD_TYPE.API_SELECT,
                  id: "reason",
                  title: "Reason",
                  label: "",
                  placeholder: "Select Reason for Cancellation",
                  fetchAction: () =>
                    getAllReasonsByType(this.props.reasonType || REASON_TYPES.campaignCancel),
                  parseResults: (response) => {
                    return response.data.map((item) => ({
                      id: item.id,
                      value: item.reason,
                    }));
                  },
                  selected: (list) => list.filter((item) => item.id === reason),
                  singleSelect: true,
                  showLabelInButton: false,
                  onSelect: ([value]) => {
                    this.inputOnChange(value.id, "reason");
                  },
                  error: hasError && !reason,
                  errorMessage: "Reason Cannot be Empty",
                },
                {
                  id: "comments",
                  type: FORM_FIELD_TYPE.TEXT_AREA,
                  title: "Comments",
                  placeholder: "Enter Comments",
                  rows: 3,
                  value: comments,
                  onChange: (e) => this.inputOnChange(e.target.value, "comments"),
                },
              ]}
            />
          </div>
          <div className={styles.calloutInfo}>
            <InfoFilledIcon />
            <span>{calloutText}</span>
          </div>
        </div>
      </RightPanel>
    );
  };
}
